import { FormikProps } from 'formik';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import Select from 'react-select';
import { Row, Col, Label, Input, FormGroup } from 'reactstrap';
import {
  CheckboxOption,
  CheckboxOptionWithImg,
} from 'Components/pos2u/CustomReactSelectComponents/CustomReactSelectOption';
import { ImageWithFallback } from 'Components/pos2u/ImageWithFallback';
import Cleave from 'cleave.js/react';
import {
  PROMO_PRODUCTS_LIMIT,
  PROMO_CATEGORY_LIMIT,
  PromoFreeCondUnitOptions,
  PromoFreeTypeOptions,
} from 'pages/Promotions/PromoConstant';
import styles from 'pages/Promotions/styles/promo.module.css';
import { getSearchProductsList } from 'services/productService';
import { RootState } from 'slices';
import {
  setselectMultiFreePromoCategories,
  setselectMultiFreePromoProducts,
} from 'slices/promotions/reducer';
import { MultiSelectStyles } from 'styles/common/multiSelectStyle';
import {
  InputActionMeta,
  SelectedData,
  SelectOptions,
} from 'types/common/FormTypes';
import { PromoFormValues } from 'types/promotion/promotionTypes';
import { transformProductData2Options } from 'utils/mappers/productMapper';
import { FaImage } from 'react-icons/fa';

const FREE_COND_INPUT_COL = 5;
const FREE_GIFT_TYPE = {
  PRODUCT: '1',
  CATEGORY: '2',
} as const;
const CONDITION_TYPE = {
  AMOUNT: '2',
} as const;

const getSelectedValues = (data: SelectedData[] | null | undefined) => {
  if (!data || !Array.isArray(data)) {
    return [];
  }

  return data.map(({ value }) => value);
};

const getPromoFreeListError = ({
  formik,
  showAllValidateErrors,
}: {
  formik: FormikProps<PromoFormValues>;
  showAllValidateErrors: boolean;
}) => {
  const freeListError = formik.errors.promoFreeGift?.freeList;
  const shouldShowError =
    (formik.touched.promoFreeGift?.freeList &&
      typeof freeListError === 'string') ||
    (!formik.isValid && freeListError && showAllValidateErrors);

  if (!shouldShowError || typeof freeListError !== 'string') {
    return null;
  }

  return freeListError;
};

const ProductOptionLabel = ({ option }: { option: SelectedData }) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <div
      style={{
        width: '2rem',
        height: '2rem',
        overflow: 'hidden',
        display: 'inline-block',
        marginRight: '10px',
        transition: 'transform 0.3s ease',
      }}
      className="image-container"
    >
      {option.image ? (
        <ImageWithFallback
          src={option.image}
          alt={option.label}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 0.3s ease',
          }}
        />
      ) : (
        <FaImage
          style={{
            fontSize: '2rem',
            marginRight: '10px',
          }}
          color="gray"
        />
      )}
    </div>
    {option.label}
  </div>
);

const PromoFreeGift = ({
  formik,
}: {
  formik: FormikProps<PromoFormValues>;
}) => {
  const { t } = useTranslation();
  const dispatch: ThunkDispatch<RootState, void, Action> = useDispatch();

  const promotionsState = useSelector((state: RootState) => state.Promotions);
  const layoutState = useSelector((state: RootState) => state.Layout);
  const infoState = useSelector((state: RootState) => state.Info);
  const { merchant, isMerchantLoaded } = infoState;
  const {
    showAllValidateErrors,
    ProductCategoryOptions,
    selectMultiFreePromoCategories,
    selectMultiFreePromoProducts,
  } = promotionsState;

  const isDarkMode = layoutState.layoutModeType === 'dark';

  const [showExceedCategoryLimitError, setShowExceedCategoryLimitError] =
    useState(false);
  const [showExceedProductLimitError, setShowExceedProductLimitError] =
    useState(false);
  const [inputValue, setInputValue] = useState('');
  const [inputProductValue, setInputProductValue] = useState('');
  const [productOptions, setProductOptions] = useState<SelectOptions[]>([]);

  const thresholdRef = useRef<HTMLInputElement | null>(null);
  const freeQtyRef = useRef<HTMLInputElement | null>(null);

  const condOptions = PromoFreeCondUnitOptions(t);
  const freeType = formik.values.promoFreeGift?.freeType;
  const isProducts = freeType === FREE_GIFT_TYPE.PRODUCT;
  const isCategory = freeType === FREE_GIFT_TYPE.CATEGORY;
  const selectedCondition = condOptions.find(
    (option) => option.value === formik.values.promoFreeGift.conditionType
  );
  const promoFreeListError = getPromoFreeListError({
    formik,
    showAllValidateErrors,
  });

  const handleInputChange = (newValue: string, meta: InputActionMeta) => {
    if (meta.action === 'input-change') {
      setInputValue(newValue);
    }
  };

  const handleSearch = useCallback(
    async (term: string) => {
      if (!isMerchantLoaded || !merchant?.id || term.length < 3) return;

      try {
        const results = await getSearchProductsList(`${merchant.id}`, term);

        if (!results) {
          setProductOptions([]);
          return;
        }

        const { message, error_code, data } = results;

        if (message !== 'OK' || error_code !== 0 || !data?.products) {
          setProductOptions([]);
          return;
        }

        const searchResultsOptions = await transformProductData2Options(
          data.products
        );
        setProductOptions(searchResultsOptions[0]?.options ?? []);
      } catch {
        setProductOptions([]);
      }
    },
    [merchant?.id, isMerchantLoaded]
  );

  const handleInputProductChange = (newValue: string) => {
    setInputProductValue(newValue);
    handleSearch(newValue);
  };

  const syncFreeGiftSelection = async (
    selectedOptions: SelectedData[],
    action:
      | typeof setselectMultiFreePromoCategories
      | typeof setselectMultiFreePromoProducts
  ) => {
    dispatch(action(selectedOptions));
    await formik.setFieldValue(
      'promoFreeGift.freeList',
      getSelectedValues(selectedOptions)
    );
    await formik.setFieldTouched('promoFreeGift.freeList', true);
  };

  const resetFreeGiftSelection = async () => {
    setShowExceedCategoryLimitError(false);
    setShowExceedProductLimitError(false);
    dispatch(setselectMultiFreePromoProducts([]));
    dispatch(setselectMultiFreePromoCategories([]));
    await formik.setFieldValue('promoFreeGift.freeList', []);
  };

  const handleChangePromoCategory = async (selectedOptions: SelectedData[]) => {
    const hasSelectAll = selectedOptions.some(
      (option) => option.value === 'select_all'
    );
    const normalizedOptions = hasSelectAll
      ? (ProductCategoryOptions[0]?.options ?? [])
      : selectedOptions;

    if (normalizedOptions.length > PROMO_CATEGORY_LIMIT) {
      setShowExceedCategoryLimitError(true);
      return;
    }

    setShowExceedCategoryLimitError(false);
    await syncFreeGiftSelection(
      normalizedOptions,
      setselectMultiFreePromoCategories
    );
  };

  const handleChangePromoProduct = async (selectedOptions: SelectedData[]) => {
    if (selectedOptions.length > PROMO_PRODUCTS_LIMIT) {
      setShowExceedProductLimitError(true);
      return;
    }

    setShowExceedProductLimitError(false);
    await syncFreeGiftSelection(
      selectedOptions,
      setselectMultiFreePromoProducts
    );
  };

  return (
    <>
      <div className="mb-3 row">
        <Label
          htmlFor="promoFreeGift.conditionType"
          className="col-sm-3 col-form-label"
        >
          {t('min_req')} <span style={{ color: 'red' }}>*</span>
        </Label>
        <Col sm={9}>
          <div className="mt-2 mb-3">
            <FormGroup>
              {condOptions.map((option) => {
                const isSelected =
                  formik.values.promoFreeGift.conditionType === option.value;

                return (
                  <div
                    key={option.value}
                    className="form-check form-check-inline"
                  >
                    <Input
                      className="form-check-input"
                      type="radio"
                      name="promoFreeGift.conditionType"
                      id={`${option.label}-rad`}
                      value={option.value}
                      checked={isSelected}
                      onChange={formik.handleChange}
                    />
                    <Label
                      className="form-check-label"
                      htmlFor={`${option.label}-rad`}
                    >
                      {option.label}
                    </Label>
                  </div>
                );
              })}
            </FormGroup>
          </div>

          {selectedCondition ? (
            <div className="mb-3">
              <Col
                xs={12}
                sm={FREE_COND_INPUT_COL}
                className="mb-2 mb-xs-0 px-0"
              >
                <div className="input-container px-0">
                  <div className="input-group">
                    <span
                      role="button"
                      onClick={() => thresholdRef.current?.focus()}
                      className="input-group-text text-primary d-flex align-items-center justify-content-center"
                      style={{ width: '2.5rem' }}
                    >
                      {selectedCondition.value === CONDITION_TYPE.AMOUNT ? (
                        '฿'
                      ) : (
                        <div className="d-flex align-items-center justify-content-center">
                          <div
                            style={{ fontSize: '0.875rem', fontWeight: 'bold' }}
                          >
                            Qty
                          </div>
                        </div>
                      )}
                    </span>

                    <Cleave
                      key={selectedCondition.value}
                      htmlRef={(el: HTMLInputElement) =>
                        (thresholdRef.current = el)
                      }
                      className="form-control promoFreeGift.thresholdValue"
                      name="promoFreeGift.thresholdValue"
                      options={
                        selectedCondition.value === CONDITION_TYPE.AMOUNT
                          ? {
                              numeral: true,
                              numeralDecimalScale: 2,
                              numeralIntegerScale: 15,
                              numeralThousandsGroupStyle: 'thousand',
                            }
                          : {
                              numeral: true,
                              numeralDecimalScale: 0,
                              numeralIntegerScale: 15,
                              numeralThousandsGroupStyle: 'thousand',
                            }
                      }
                      value={formik.values.promoFreeGift.thresholdValue}
                      onChange={(e: any) =>
                        formik.setFieldValue(
                          'promoFreeGift.thresholdValue',
                          e.target.rawValue
                        )
                      }
                      onBlur={formik.handleBlur}
                    />
                  </div>
                </div>
              </Col>
            </div>
          ) : null}
        </Col>
      </div>

      <div className="mb-3 row">
        <Label
          htmlFor="promoFreeGift.freeType"
          className="col-sm-3 col-form-label"
        >
          {t('Free gift')} <span style={{ color: 'red' }}>*</span>
        </Label>
        <Col sm={9}>
          <div className="mt-2 mb-3">
            <FormGroup>
              {PromoFreeTypeOptions(t).map(
                (option: { label: string; value: string }) => (
                  <div
                    key={option.value}
                    className="form-check form-check-inline"
                  >
                    <Input
                      className="form-check-input"
                      type="radio"
                      name="promoFreeGift.freeType"
                      id={`${option.label}-rad`}
                      value={option.value}
                      checked={
                        formik.values.promoFreeGift.freeType === option.value
                      }
                      onChange={async (e) => {
                        formik.handleChange(e);
                        await resetFreeGiftSelection();
                      }}
                    />
                    <Label
                      className="form-check-label"
                      htmlFor={`${option.label}-rad`}
                    >
                      {option.label}
                    </Label>
                  </div>
                )
              )}
            </FormGroup>
          </div>
        </Col>
      </div>

      <div
        className={`mb-3 row ${isCategory ? '' : 'd-none'}`}
        data-testid="fg-category"
      >
        <Label
          htmlFor="FreeProductList"
          className="col-sm-3 col-form-label"
        ></Label>
        <Col sm={9}>
          <Select
            id="freeCategoryList"
            name="freeCategoryList"
            aria-label="fg-category"
            value={selectMultiFreePromoCategories}
            isMulti={true}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            onChange={handleChangePromoCategory}
            options={ProductCategoryOptions}
            components={{ Option: CheckboxOption }}
            styles={MultiSelectStyles(isDarkMode)}
            placeholder={t('PleaseSelect')}
          />
          {showExceedCategoryLimitError ? (
            <div className="text-danger mt-1">
              {`${t('Cannot select more than')} ${PROMO_CATEGORY_LIMIT} ${t(
                'text_items'
              )}`}
            </div>
          ) : promoFreeListError ? (
            <div className="text-danger mt-1">{promoFreeListError}</div>
          ) : null}
        </Col>
      </div>

      <div
        className={`mb-3 row ${isProducts ? '' : 'd-none'}`}
        data-testid="fg-products"
      >
        <Label
          htmlFor="FreeProductList"
          className="col-sm-3 col-form-label"
        ></Label>
        <Col sm={9}>
          <Select
            id="freeProductList"
            name="freeProductList"
            aria-label="fg-products"
            components={{ Option: CheckboxOptionWithImg }}
            value={selectMultiFreePromoProducts}
            isMulti={true}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            onChange={handleChangePromoProduct}
            inputValue={inputProductValue}
            onInputChange={handleInputProductChange}
            options={productOptions}
            formatOptionLabel={(option: SelectedData) => (
              <ProductOptionLabel option={option} />
            )}
            styles={MultiSelectStyles(isDarkMode)}
            placeholder={t('PleaseSelect')}
          />
          {showExceedProductLimitError ? (
            <div className="text-danger mt-1">
              {`${t('Cannot select more than')} ${PROMO_PRODUCTS_LIMIT} ${t(
                'text_items'
              )}`}
            </div>
          ) : promoFreeListError ? (
            <div className="text-danger mt-1">{promoFreeListError}</div>
          ) : null}
        </Col>
      </div>

      <div className="mb-3 row">
        <Label
          htmlFor="usage_limit"
          className={`col-sm-3 col-form-label ${styles['text-pd']} px-1`}
        ></Label>
        <Col sm={9}>
          <div className="mt-3 mb-3">
            <Col xs={12} sm={FREE_COND_INPUT_COL}>
              <div className="input-container">
                <div className="input-group">
                  <span
                    role="button"
                    onClick={() => freeQtyRef.current?.focus()}
                    className="input-group-text text-primary d-flex align-items-center justify-content-center"
                    style={{ width: '2.5rem' }}
                  >
                    <div className="d-flex align-items-center justify-content-center">
                      <div style={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                        Qty
                      </div>
                    </div>
                  </span>

                  <Cleave
                    key="freeQty"
                    htmlRef={(el: HTMLInputElement) =>
                      (freeQtyRef.current = el)
                    }
                    className="form-control promoFreeGift.freeQty"
                    name="promoFreeGift.freeQty"
                    options={{
                      numeral: true,
                      numeralDecimalScale: 0,
                      numeralPositiveOnly: true,
                    }}
                    value={formik.values.promoFreeGift.freeQty}
                    onChange={(e: any) =>
                      formik.setFieldValue(
                        'promoFreeGift.freeQty',
                        e.target.rawValue
                      )
                    }
                    onBlur={formik.handleBlur}
                  />
                </div>
                {(formik.touched.promoFreeGift?.freeQty &&
                  formik.errors.promoFreeGift?.freeQty) ||
                (!formik.isValid &&
                  formik.errors.promoFreeGift?.freeQty &&
                  showAllValidateErrors) ? (
                  <div className="text-danger">
                    {formik.errors.promoFreeGift.freeQty}
                  </div>
                ) : null}
              </div>
            </Col>
          </div>
        </Col>
      </div>

      <div className="mb-3 row">
        <Label
          htmlFor="usage_limit"
          className={`col-sm-3 col-form-label ${styles['text-pd']} px-1`}
        >
          {t('Usage limit')} <span style={{ color: 'red' }}>*</span>
        </Label>
        <Col xs={12} sm={9} lg={9} className="mb-2 mb-xs-0">
          <Row className="align-items-top mb-0 mb-xs-0 px-0 mx-0 justify-content-space-between">
            <Col xs={12} sm={5} className="mb-0 mb-xs-0 px-0">
              <div className="input-container">
                <Cleave
                  className={`form-control ${styles['no-px']}`}
                  name="usage_limit"
                  options={{
                    numeral: true,
                    numeralDecimalScale: 0,
                    numeralIntegerScale: 2,
                    numeralPositiveOnly: true,
                  }}
                  value={formik.values.usage_limit}
                  onChange={(e: any) => {
                    formik.setFieldValue('usage_limit', e.target.rawValue);
                  }}
                  onBlur={formik.handleBlur}
                  required
                />
                {(formik.touched.usage_limit && formik.errors.usage_limit) ||
                (!formik.isValid &&
                  formik.errors.usage_limit &&
                  showAllValidateErrors) ? (
                  <div className="text-danger">{formik.errors.usage_limit}</div>
                ) : null}
              </div>
            </Col>

            <Col xs={12} sm={7} className="mb-0 mb-xs-0 px-0">
              <div className="px-2">{t('usage_limit_description')}</div>
            </Col>
          </Row>
        </Col>
      </div>
    </>
  );
};

export default PromoFreeGift;
